import { XMarkIcon } from "@heroicons/react/24/outline";
import type { FormEvent } from "react";
import { twMerge } from "tailwind-merge";

export type AdminSeniorVoAddTeamFormValues = { teamName: string; seniorVoName: string; seniorVoEmail: string };
export type AdminSeniorVoAddTeamFormErrors = Partial<Record<keyof AdminSeniorVoAddTeamFormValues, string>>;

interface Props {
  values: AdminSeniorVoAddTeamFormValues;
  errors: AdminSeniorVoAddTeamFormErrors;
  saving: boolean;
  onCancel: () => void;
  onChange: (field: keyof AdminSeniorVoAddTeamFormValues, value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
}

const AdminSeniorVoAddTeamModal = ({ values, errors, saving, onCancel, onChange, onSubmit }: Props) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="admin-senior-vo-add-team-title"
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
  >
    <section className="w-full max-w-xl rounded-[28px] bg-white shadow-[0_40px_120px_-48px_rgba(16,42,67,0.55)]">
      <header className="flex items-start justify-between gap-4 border-b border-[#E4ECE9] px-6 py-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0B8A7C]">Senior VO Team</p>
          <h2 id="admin-senior-vo-add-team-title" className="mt-1 text-2xl font-semibold text-[#102A43]">
            Add Team
          </h2>
          <p className="mt-2 text-sm text-[#52606D]">Create a new self-reporting Senior VO team.</p>
        </div>
        <button
          type="button"
          aria-label="Close add team modal"
          disabled={saving}
          onClick={onCancel}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#52606D] transition-colors hover:bg-[#F7FAFC] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>

      <form onSubmit={onSubmit} className="px-6 py-5">
        <div className="space-y-5">
          <div>
            <label htmlFor="admin-senior-vo-team-name" className="mb-2 block text-sm font-semibold text-[#243B53]">
              Team Name
            </label>
            <input
              id="admin-senior-vo-team-name"
              type="text"
              value={values.teamName}
              onChange={(event) => onChange("teamName", event.target.value)}
              className={twMerge(
                "h-12 w-full rounded-xl border bg-white px-4 text-sm text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]",
                errors.teamName ? "border-[#BF0002]" : "border-[#D9E2EC]",
              )}
              placeholder="Enter unique team name"
            />
            {errors.teamName && <p className="mt-2 text-sm text-[#BF0002]">{errors.teamName}</p>}
          </div>

          <div>
            <label htmlFor="admin-senior-vo-name" className="mb-2 block text-sm font-semibold text-[#243B53]">
              Senior VO Name
            </label>
            <input
              id="admin-senior-vo-name"
              type="text"
              value={values.seniorVoName}
              onChange={(event) => onChange("seniorVoName", event.target.value)}
              className={twMerge(
                "h-12 w-full rounded-xl border bg-white px-4 text-sm text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]",
                errors.seniorVoName ? "border-[#BF0002]" : "border-[#D9E2EC]",
              )}
              placeholder="Enter Senior VO name"
            />
            {errors.seniorVoName && <p className="mt-2 text-sm text-[#BF0002]">{errors.seniorVoName}</p>}
          </div>

          <div>
            <label htmlFor="admin-senior-vo-email" className="mb-2 block text-sm font-semibold text-[#243B53]">
              Senior VO Email
            </label>
            <input
              id="admin-senior-vo-email"
              type="email"
              value={values.seniorVoEmail}
              onChange={(event) => onChange("seniorVoEmail", event.target.value)}
              className={twMerge(
                "h-12 w-full rounded-xl border bg-white px-4 text-sm text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]",
                errors.seniorVoEmail ? "border-[#BF0002]" : "border-[#D9E2EC]",
              )}
              placeholder="Enter Senior VO email"
            />
            {errors.seniorVoEmail && <p className="mt-2 text-sm text-[#BF0002]">{errors.seniorVoEmail}</p>}
          </div>
        </div>

        <footer className="mt-8 flex items-center justify-end gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="inline-flex h-11 items-center justify-center rounded-xl border border-[#D9E2EC] bg-white px-5 text-sm font-semibold text-[#52606D] transition-colors hover:bg-[#F7FAFC] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-11 items-center justify-center rounded-xl bg-[#0B8A7C] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#0A796D] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add"}
          </button>
        </footer>
      </form>
    </section>
  </div>
);

export default AdminSeniorVoAddTeamModal;
